import {
    Box,
    Flex,
    SlideFade,
    useDisclosure,
    Picture,
    Text,
    Button,
    useOutsideClick,
} from "@smile2impress/components/relaunch";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import { isClickedOnButton } from "components/Header/components/LocationSelect/helpers";
import { FlagIconByLocale } from "components/Icons/FlagIconByLocale";
import Link from "components/Link";
import { isHeaderNavLinkActive } from "utils/isHeaderNavLinkActive";

import { MenuItem, MenuItemGroup } from "../../Header.types";
import { LocationMenu } from "../LocationMenu/LocationMenu";
import { MenuItem as MenuItemComponent } from "./components/MenuItem";
import { DesktopHeaderProps } from "./DesktopHeader.types";
import { CompactMenu } from "./components/CompactMenu";

export const DesktopHeader: FC<DesktopHeaderProps> = ({
    items,
    cta,
    candidate,
    locationSelect,
    menuPlaceholderRef,
    isFixedPosition,
    hasFixedBanner,
    hideMenu = false,
}) => {
    const router = useRouter();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const {
        isOpen: isLocationServiceOpen,
        onClose: onLocationServiceClose,
        onToggle: onLocationServiceToggle,
    } = useDisclosure();
    const [withCompactMenu, setCompactMenu] = useState<boolean>(false);
    const [activeMenu, setMenu] = useState<number>(-1);
    const [menuRight, setMenuRight] = useState<number | string>(2);
    const [collapsableItems, setItems] = useState<(MenuItemGroup | MenuItem)[]>(
        []
    );
    const menuRef = useRef<HTMLDivElement>(null);
    const locationContentDesktopRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);
    const { locale } = useUTSLocaleContext();

    const menuItemsGroups = items.filter(
        (item) => (item as MenuItemGroup).items?.length > 0
    );
    const menuItems: MenuItem[] = (items as MenuItem[]).filter(
        (item: MenuItem | MenuItemGroup) => !item.hasOwnProperty("items")
    );

    const onToggleMenu = ({
        items,
        index,
        isOpen,
    }: {
        items: (MenuItemGroup | MenuItem)[];
        index: number;
        isOpen: boolean;
    }) => {
        setMenu(index);
        if (index !== activeMenu) {
            onClose();
            setItems(items as MenuItem[]);
            onOpen();
        } else {
            if (isOpen) onOpen();
            else {
                onClose();
                setMenu(-1);
            }
        }
    };

    useOutsideClick({
        ref: locationContentDesktopRef,
        handler: (e: Event) => {
            if (
                e?.target &&
                !isClickedOnButton(e.target as HTMLInputElement, 5)
            )
                onLocationServiceClose();
        },
    });

    useOutsideClick({
        ref: submenuRef,
        handler: (e: Event) => {
            if (
                e?.target &&
                !isClickedOnButton(e.target as HTMLInputElement, 5)
            ) {
                onClose();
                setItems([]);
                setMenu(-1);
            }
        },
    });

    const checkforMenuSize = () => {
        if (
            (menuRef?.current?.offsetWidth || 0) >
            menuPlaceholderRef?.current?.offsetWidth
        ) {
            setCompactMenu(true);
        }
        const parentNode = menuPlaceholderRef?.current
            ?.parentNode as HTMLElement | null;
        if (!parentNode) return;
        const { left } = parentNode.getBoundingClientRect();

        if (left) {
            setMenuRight(`${left}px`);
        }
    };

    useEffect(() => {
        onClose();
        setItems([]);
        setMenu(-1);
    }, [router.asPath]);

    useEffect(() => {
        checkforMenuSize();
        window.addEventListener("resize", checkforMenuSize);

        return () => window.removeEventListener("resize", checkforMenuSize);
    }, []);

    return (
        <>
            {!hideMenu && (
                <Box
                    position={isFixedPosition ? "fixed" : "absolute"}
                    zIndex="docked"
                    top={!isFixedPosition ? 0 : hasFixedBanner ? 4 : 2}
                    right={menuRight}
                    h={4}
                    bgColor="base.grey"
                    _dark={{
                        bgColor: "base.greyDark",
                    }}
                    borderRadius="sm"
                    px={1}
                    py={0.7}
                    ref={menuRef}
                >
                    <Flex direction="row" gap={1} position="relative">
                        {menuItemsGroups.map((item, index) => (
                            <Fragment key={index}>
                                <MenuItemComponent
                                    {...item}
                                    cta={cta}
                                    index={index}
                                    activeIndex={activeMenu}
                                    onToggleMenu={onToggleMenu}
                                />
                            </Fragment>
                        ))}
                        {withCompactMenu ? (
                            <CompactMenu
                                activeIndex={activeMenu}
                                index={1000}
                                cta={cta}
                                onToggleMenu={onToggleMenu}
                                items={menuItems}
                            />
                        ) : (
                            menuItems.map((item, index) => (
                                <Fragment key={index}>
                                    <MenuItemComponent
                                        {...item}
                                        activeIndex={activeMenu}
                                        index={menuItemsGroups.length + index}
                                        cta={cta}
                                    />
                                </Fragment>
                            ))
                        )}
                        <Flex gap={2} direction="row" pl="4px">
                            {candidate?.link && (
                                <Button
                                    as={Link}
                                    variant="white"
                                    href={candidate.link}
                                    opacity={activeMenu >= 0 ? 0.4 : 1}
                                    height={["36px"]}
                                >
                                    <Text
                                        as="span"
                                        textStyle="captionSmall"
                                        color="inherit"
                                    >
                                        {candidate.text}
                                    </Text>
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                onClick={onLocationServiceToggle}
                                px={0}
                                height={["36px"]}
                            >
                                <FlagIconByLocale locale={locale} />
                            </Button>
                        </Flex>
                    </Flex>

                    <Box
                        minWidth={["350px"]}
                        zIndex="dropdown"
                        position="absolute"
                        flexGrow={1}
                        top={"100%"}
                        mt={0.7}
                        right={0}
                        height={"auto"}
                        borderRadius="sm"
                        overflow="hidden"
                        justifyContent="center"
                        backgroundColor="bg.base"
                        visibility={
                            isLocationServiceOpen ? "visible" : "hidden"
                        }
                        ref={locationContentDesktopRef}
                        boxShadow="medium"
                    >
                        <LocationMenu
                            locale={locale}
                            {...locationSelect}
                            closeMenu={onLocationServiceClose}
                        />
                    </Box>

                    {collapsableItems?.length > 0 && (
                        <Box
                            position="absolute"
                            top="4"
                            left={0}
                            right={0}
                            mt="2px"
                            ref={submenuRef}
                        >
                            <SlideFade in={isOpen}>
                                <Box
                                    bgColor="bg.base"
                                    borderRadius="sm"
                                    overflow="hidden"
                                    boxShadow="large"
                                    p={3}
                                >
                                    <Flex
                                        columnGap={1}
                                        rowGap={3}
                                        flexWrap="wrap"
                                        justifyContent="center"
                                    >
                                        {collapsableItems.map((item, index) => {
                                            const itemLink = (item as MenuItem)
                                                .link;
                                            const isSubmenuLinkActive =
                                                isHeaderNavLinkActive(
                                                    router,
                                                    itemLink,
                                                    locale
                                                );
                                            return (
                                                <Link
                                                    key={index}
                                                    href={itemLink}
                                                    display="flex"
                                                    textAlign="center"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                    flexGrow={0}
                                                    flexBasis={
                                                        collapsableItems.length %
                                                            2 ===
                                                        0
                                                            ? "45%"
                                                            : "30%"
                                                    }
                                                    gap="8px"
                                                    color="text.base"
                                                    _hover={{
                                                        color: "base.blue",
                                                    }}
                                                    {...(isSubmenuLinkActive && {
                                                        color: "base.blue",
                                                    })}
                                                    _dark={{
                                                        _hover: {
                                                            color: "base.bitterSweet",
                                                        },
                                                        ...(isSubmenuLinkActive && {
                                                            color: "base.bitterSweet",
                                                        }),
                                                    }}
                                                >
                                                    <Box
                                                        as="span"
                                                        display="inline-block"
                                                        position="relative"
                                                        overflow="hidden"
                                                        borderRadius="50%"
                                                        h={4}
                                                        w={4}
                                                    >
                                                        <Box
                                                            position="absolute"
                                                            bgColor="base.halfBlack"
                                                            opacity="0.5"
                                                            top="0"
                                                            left="0"
                                                            h={4}
                                                            w={4}
                                                            zIndex={0}
                                                        ></Box>
                                                        <Picture
                                                            {...item.image}
                                                            position="relative"
                                                            zIndex={1}
                                                        />
                                                    </Box>
                                                    <Text
                                                        as="span"
                                                        textStyle="caption"
                                                        fontWeight="bold"
                                                        whiteSpace="wrap"
                                                        color="inherit"
                                                    >
                                                        {item.text}
                                                    </Text>
                                                </Link>
                                            );
                                        })}
                                    </Flex>
                                    <Button
                                        mt={3}
                                        as={Link}
                                        variant="red"
                                        {...(cta.onClick
                                            ? { onClick: cta.onClick }
                                            : {})}
                                        {...(cta.link
                                            ? { href: cta.link }
                                            : {})}
                                        width="100%"
                                    >
                                        <Text
                                            as="span"
                                            textStyle="captionSmall"
                                            color="inherit"
                                        >
                                            {cta.text}
                                        </Text>
                                    </Button>
                                </Box>
                            </SlideFade>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};
